import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, isApiClientError } from '../lib/api';
import { queryKeys } from '../lib/query';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ErrorBanner, Loading } from '../components/Status';

type State = { readonly phase: 'working' } | { readonly phase: 'done' } | { readonly phase: 'error'; readonly error: unknown };

/**
 * `/auth/callback?token=…` — SPA zuzywa token przez `GET /api/auth/callback` z
 * naglowkiem `Accept: application/json` (bez niego Worker przekierowuje na `/`,
 * co jest sciezka dla klikniecia w kliencie pocztowym).
 *
 * Token jest jednorazowy, wiec zapytanie leci DOKLADNIE raz — `ref` chroni przed
 * podwojnym efektem w StrictMode, ktory zuzylby token i pokazal
 * `token_already_used`.
 */
export function AuthCallbackPage({ token, onDone }: { readonly token: string | null; readonly onDone: () => void }) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<State>({ phase: 'working' });
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    if (token === null || token.length === 0) {
      setState({ phase: 'error', error: new Error('Brak tokenu w adresie.') });
      return;
    }

    void (async () => {
      try {
        await api.auth.callback(token);
        await queryClient.invalidateQueries({ queryKey: queryKeys.me });
        setState({ phase: 'done' });
        onDone();
      } catch (error) {
        setState({ phase: 'error', error });
      }
    })();
  }, [token, queryClient, onDone]);

  if (state.phase === 'working') {
    return (
      <main className="mx-auto flex min-h-full max-w-md items-center justify-center p-8">
        <Loading label="Loguje…" />
      </main>
    );
  }

  if (state.phase === 'done') {
    return (
      <main className="mx-auto flex min-h-full max-w-md items-center justify-center p-8">
        <Loading label="Gotowe, przekierowuje…" />
      </main>
    );
  }

  const expired = isApiClientError(state.error) && state.error.code === 'token_expired';
  return (
    <main className="mx-auto flex min-h-full max-w-md flex-col justify-center gap-4 p-8">
      <Card>
        <CardHeader>
          <CardTitle>{expired ? 'Link wygasl' : 'Nie udalo sie zalogowac'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <ErrorBanner error={state.error} />
          <Button variant="brand" size="sm" onClick={onDone}>
            Wyslij nowy link
          </Button>
        </CardContent>
      </Card>
    </main>
  );
}

export default AuthCallbackPage;
